import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Phone, ShoppingCart, CreditCard, MapPin, Navigation, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import CustomerAvatar from '@/components/customers/CustomerAvatar';
import CustomerKpiStrip from '@/components/customers/CustomerKpiStrip';
import OverviewTab from '@/components/customers/tabs/OverviewTab';
import OrdersTab from '@/components/customers/tabs/OrdersTab';
import PaymentsTab from '@/components/customers/tabs/PaymentsTab';
import VisitsTab from '@/components/customers/tabs/VisitsTab';
import InfoTab from '@/components/customers/tabs/InfoTab';
import { api } from '@/api/apiClient';
import { cur } from '@/lib/currency';

const TABS = [
  { key: 'overview', label: 'نظرة عامة' },
  { key: 'orders',   label: 'الطلبات' },
  { key: 'payments', label: 'التحصيلات' },
  { key: 'visits',   label: 'الزيارات' },
  { key: 'info',     label: 'المعلومات' },
];

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const customerId = parseInt(id);

  const [customer, setCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('overview');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [custRes, ordersRes, visitsRes] = await Promise.all([
        api.get(`/customers/${customerId}`),
        api.get(`/customers/${customerId}/orders`, { limit: 50 }),
        api.get(`/customers/${customerId}/visits`),
      ]);
      if (custRes?.success) setCustomer(custRes.data);
      else throw new Error(custRes?.error || 'فشل تحميل بيانات العميل');
      setOrders(ordersRes?.success ? (ordersRes.data || []) : []);
      setVisits(visitsRes?.success ? (visitsRes.data || []) : []);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [id]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">جاري التحميل...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="p-6 flex flex-col items-center gap-4">
      <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center">
        <AlertCircle className="w-8 h-8 text-red-500" />
      </div>
      <p className="font-semibold text-foreground text-center">{error}</p>
      <Button onClick={load} variant="outline">إعادة المحاولة</Button>
    </div>
  );

  if (!customer) return null;

  const phone = customer.phone || customer.mobile;
  const city = [customer.city, Array.isArray(customer.state_id) ? customer.state_id[1] : null].filter(Boolean).join('، ');
  const hasCoords = customer.partner_latitude && customer.partner_longitude;
  const mapsUrl = hasCoords
    ? `https://www.google.com/maps?q=${customer.partner_latitude},${customer.partner_longitude}`
    : city ? `https://www.google.com/maps/search/${encodeURIComponent(city)}` : null;
  const ordersTotal = orders.filter(o => ['sale', 'done'].includes(o.state)).length;
  const totalValue = orders.filter(o => ['sale', 'done'].includes(o.state)).reduce((s, o) => s + (o.amount_total || 0), 0);
  const debt = customer.debit || 0;

  return (
    <div className="flex flex-col min-h-full" dir="rtl">
      {/* Hero Card */}
      <div className="bg-gradient-to-br from-primary to-primary/80 text-white px-4 pt-4 pb-5">
        <div className="flex items-start gap-4">
          <CustomerAvatar name={customer.name} size="lg" />
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold leading-snug">{customer.name}</h1>
            {customer.ref && (
              <p className="text-primary-foreground/70 text-xs font-mono mt-0.5"># {customer.ref}</p>
            )}
            {city && (
              <div className="flex items-center gap-1 mt-1">
                <MapPin className="w-3.5 h-3.5 text-white/70" />
                <span className="text-sm text-white/80">{city}</span>
              </div>
            )}
            {debt > 0 && (
              <div className="mt-2 inline-flex items-center gap-1.5 bg-red-500/30 border border-red-400/40 rounded-xl px-2.5 py-1">
                <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
                <span className="text-xs font-bold text-white">متأخرات: {Number(debt).toLocaleString('ar-EG', { maximumFractionDigits: 0 })} {cur()}</span>
              </div>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="grid grid-cols-5 gap-1.5 mt-4">
          {phone && (
            <a href={`tel:${phone}`} className="flex flex-col items-center gap-1 bg-white/15 hover:bg-white/25 rounded-xl p-2 transition-colors">
              <Phone className="w-4 h-4" />
              <span className="text-xs">اتصال</span>
            </a>
          )}
          {phone && (
            <a href={`https://wa.me/${phone?.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer"
              className="flex flex-col items-center gap-1 bg-white/15 hover:bg-white/25 rounded-xl p-2 transition-colors">
              <span className="text-base">💬</span>
              <span className="text-xs">واتساب</span>
            </a>
          )}
          {mapsUrl && (
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
              className="flex flex-col items-center gap-1 bg-white/15 hover:bg-white/25 rounded-xl p-2 transition-colors">
              <Navigation className="w-4 h-4" />
              <span className="text-xs">اتجاه</span>
            </a>
          )}
          <Link to={`/orders/new?customer_id=${id}&customer_name=${encodeURIComponent(customer.name)}`}
            className="flex flex-col items-center gap-1 bg-white/15 hover:bg-white/25 rounded-xl p-2 transition-colors">
            <ShoppingCart className="w-4 h-4" />
            <span className="text-xs">طلب</span>
          </Link>
          <Link to={`/collections/new?customer_id=${id}&customer_name=${encodeURIComponent(customer.name)}`}
            className="flex flex-col items-center gap-1 bg-white/15 hover:bg-white/25 rounded-xl p-2 transition-colors">
            <CreditCard className="w-4 h-4" />
            <span className="text-xs">تحصيل</span>
          </Link>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="px-4 py-3 bg-background border-b border-border">
        <CustomerKpiStrip customer={customer} ordersTotal={ordersTotal} invoicingTotal={totalValue} />
      </div>

      {/* Tabs Navigation */}
      <div className="sticky top-0 z-10 bg-background border-b border-border">
        <div className="flex overflow-x-auto no-scrollbar">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex-shrink-0 px-4 py-3 text-sm font-semibold border-b-2 transition-all ${
                tab === t.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}>
              {t.label}
              {t.key === 'orders' && orders.length > 0 && (
                <span className="mr-1.5 text-xs bg-muted px-1.5 py-0.5 rounded-full">{orders.length}</span>
              )}
              {t.key === 'payments' && debt > 0 && (
                <span className="mr-1.5 text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full">!</span>
              )}
              {t.key === 'visits' && visits.length > 0 && (
                <span className="mr-1.5 text-xs bg-muted px-1.5 py-0.5 rounded-full">{visits.length}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 p-4 pb-24">
        {tab === 'overview' && (
          <OverviewTab customer={customer} orders={orders} visits={visits} />
        )}
        {tab === 'orders' && (
          <OrdersTab orders={orders} ordersTotal={ordersTotal} customerId={id} customerName={customer.name} />
        )}
        {tab === 'payments' && (
          <PaymentsTab customerId={customerId} customerName={customer.name} />
        )}
        {tab === 'visits' && (
          <VisitsTab visits={visits} customerId={id} customerName={customer.name} />
        )}
        {tab === 'info' && (
          <InfoTab customer={customer} />
        )}
      </div>
    </div>
  );
}